/**
 * EditableDetailCard - DetailCard powered by FormFieldMeta.
 *
 * Extends the existing DetailCard with:
 * 1. Automatic display formatting based on field_type (date, currency, etc.)
 * 2. Optional edit button that opens an EditFormDialog
 * 3. Consistent field rendering via FieldRow
 *
 * This replaces the pattern of manually mapping data to FieldRow with
 * inline formatting and ad-hoc edit state management.
 *
 * Usage:
 *   <EditableDetailCard
 *     title="Customer"
 *     icon={<UserCircle size={16} />}
 *     items={[
 *       { field: defineField({ code_name: 'name', display_name: 'Name', field_type: 'TEXT' }), value: data.name },
 *       { field: defineField({ code_name: 'email', display_name: 'Email', field_type: 'EMAIL' }), value: data.email },
 *       { field: defineField({ code_name: 'purchase_date', display_name: 'Purchase Date', field_type: 'DATE' }), value: data.purchase_date },
 *     ]}
 *     editConfig={{
 *       onSave: async (values) => { await mutation.mutateAsync(values) },
 *       isPending: mutation.isPending,
 *       canEdit: can('holdings.edit.global'),
 *       requireReason: true,
 *     }}
 *     formatOptions={{ locale, currency }}
 *   />
 */

import { type ReactNode, useState, useMemo } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { formatDisplayValue, isDisplayEmpty } from "@/shared/lib/forms/display";
import type { DisplayFormatOptions } from "@/shared/lib/forms/display";
import { useFormattingContext } from "@/shared/lib/formatting";
import { DetailCard, FieldRow } from "./detail-card";
import { normaliseDetailCardDisplayValue } from "./detail-card-formatting";
import { EditFormDialog } from "./edit-form-dialog";
import type { CardFieldItem, EditConfig } from "./editable-card-types";

interface EditableDetailCardProps {
  /** Card title. */
  title: string;
  /** Optional node rendered beside the title (see DetailCard). */
  titleAccessory?: ReactNode;
  /** Finish class for the header bar (see DetailCard). */
  headerFinishClass?: string;
  /** Card icon. */
  icon: ReactNode;
  /** Field items with metadata and values. */
  items: CardFieldItem[];
  /** Edit configuration. Omit to render read-only. */
  editConfig?: EditConfig;
  /** Locale/currency for display formatting. */
  formatOptions?: DisplayFormatOptions;
  /** Set false to never collapse (default: true). */
  collapsible?: boolean;
  /** Number of rows before collapse toggle (default: 5). */
  previewCount?: number;
  /** Additional trailing content (rendered before edit button). */
  trailing?: ReactNode;
  /** Optional stable selector for the edit button. */
  editButtonTestId?: string;
  /** Folded away to its header (see DetailCard). */
  folded?: boolean;
  /** Renders the fold control; the caller owns the state (see DetailCard). */
  onToggleFold?: () => void;
}

export function EditableDetailCard({
  title,
  titleAccessory,
  headerFinishClass,
  icon,
  items,
  editConfig,
  formatOptions,
  collapsible,
  previewCount,
  trailing,
  editButtonTestId,
  folded,
  onToggleFold,
}: EditableDetailCardProps) {
  const [editOpen, setEditOpen] = useState(false);
  const orgFormatting = useFormattingContext();
  const effectiveFormatOptions: DisplayFormatOptions = {
    locale: formatOptions?.locale ?? orgFormatting.locale,
    currency: formatOptions?.currency ?? orgFormatting.currency,
    values: Object.fromEntries(
      items.map((item) => [item.field.code_name, item.value]),
    ),
  };

  const showEditButton = editConfig && editConfig.canEdit !== false;

  // Build the edit form fields and initial values from non-readOnly items
  const { editFields, editValues } = useMemo(() => {
    const fields = items
      .filter((item) => !item.readOnly && !item.field.disabled)
      .map((item) => item.field);
    const values: Record<string, unknown> = {};
    for (const item of items) {
      if (!item.readOnly && !item.field.disabled) {
        // `editValue` lets a field open the form on an inherited default
        // while its display row keeps showing what is actually stored.
        // Object-valued types (picker, address) are re-seeded to `null` by
        // EditFormDialog, so an empty one never reaches the form as `''`.
        values[item.field.code_name] = item.editValue ?? item.value ?? "";
      }
    }
    return { editFields: fields, editValues: values };
  }, [items]);

  const trailingEl = (
    <>
      {trailing}
      {showEditButton && (
        <Button
          variant="ghost"
          size="icon-xs"
          data-testid={editButtonTestId}
          onClick={() => setEditOpen(true)}
          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
    </>
  );

  return (
    <>
      <DetailCard
        title={title}
        titleAccessory={titleAccessory}
        headerFinishClass={headerFinishClass}
        icon={icon}
        trailing={trailingEl}
        collapsible={collapsible}
        previewCount={previewCount}
        folded={folded}
        onToggleFold={onToggleFold}
      >
        {items
          .filter(
            (item) => !item.hideWhenEmptyDisplay || !isDisplayEmpty(item.value),
          )
          .map((item) => {
            const displayVal =
              item.displayValue ??
              (item.value && typeof item.value === "object"
                ? normaliseDetailCardDisplayValue(item.value)
                : formatDisplayValue(
                    item.field,
                    item.value,
                    effectiveFormatOptions,
                  ));
            const empty = isDisplayEmpty(item.value) && !item.displayValue;

            return (
              <FieldRow
                key={item.field.code_name}
                label={item.field.display_name}
                value={displayVal}
                muted={empty}
                link={item.link}
                onLinkClick={item.onLinkClick}
                positive={item.positive}
              />
            );
          })}
      </DetailCard>

      {showEditButton && editConfig && (
        <EditFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          title={`Edit ${title}`}
          fields={editFields}
          values={editValues}
          onSave={editConfig.onSave}
          isPending={editConfig.isPending}
          requireReason={editConfig.requireReason}
          submissionMode={editConfig.submissionMode}
          onFileUpload={editConfig.onFileUpload}
        />
      )}
    </>
  );
}
